import React, { useState } from 'react';

/**
 * ValidationDetailModal - Simplified 3-4 stage validation process view
 * 
 * Stage 1: Data Collection (LinkedIn Profile + Company)
 * Stage 2: Location & Company Verification
 * Stage 3: Apply Validation Rules (Final)
 * 
 * Note: Website scraping & toll-free check are data enrichment, not validation stages
 */

const VALIDATION_STAGES = [
  { id: 1, name: 'Data Collection', description: 'LinkedIn Profile & Company Data' },
  { id: 2, name: 'Verification', description: 'Location & Company Match' },
  { id: 3, name: 'Validation Rules', description: 'Final Lead Qualification' }
];

const ValidationDetailModal = ({ isOpen, onClose, record, validationResults, taskId }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedStage, setSelectedStage] = useState(null);
  const [updatingField, setUpdatingField] = useState(null);

  if (!isOpen || !record) return null;
  
  // Get task_id from record or prop
  const recordTaskId = taskId || record.task_id || (record.id ? null : null);

  // Parse validation results
  let parsedResults = null;
  try {
    if (validationResults) {
      parsedResults = typeof validationResults === 'string' 
        ? JSON.parse(validationResults) 
        : validationResults;
    } else if (record?.validation_results) {
      parsedResults = typeof record.validation_results === 'string'
        ? JSON.parse(record.validation_results)
        : record.validation_results;
    }
  } catch (e) {
    console.error('Error parsing validation results:', e);
  }

  // Parse other data
  let linkedinProfile = null;
  let linkedinCompany = null;
  let websiteData = null;
  let qualificationReasons = [];
  let phoneNumberUpdate = null;

  try {
    // Get LinkedIn profile from multiple sources (priority: stored data > parsed results > step data)
    if (record?.linkedin_profile_data) {
      try {
        linkedinProfile = typeof record.linkedin_profile_data === 'string'
          ? JSON.parse(record.linkedin_profile_data)
          : record.linkedin_profile_data;
      } catch (e) {
        console.error('Error parsing linkedin_profile_data:', e);
      }
    }
    if (!linkedinProfile && parsedResults?.linkedin_profile) {
      linkedinProfile = parsedResults.linkedin_profile;
    }
    if (!linkedinProfile && parsedResults?.steps) {
      const step1 = parsedResults.steps.find(s => s.step === 1);
      if (step1?.data && Object.keys(step1.data).length > 0) {
        linkedinProfile = step1.data;
      }
    }
    
    // Get LinkedIn company from multiple sources (priority: stored data > parsed results > step data)
    if (record?.linkedin_company_data) {
      try {
        linkedinCompany = typeof record.linkedin_company_data === 'string'
          ? JSON.parse(record.linkedin_company_data)
          : record.linkedin_company_data;
      } catch (e) {
        console.error('Error parsing linkedin_company_data:', e);
      }
    }
    if (!linkedinCompany && parsedResults?.linkedin_company) {
      linkedinCompany = parsedResults.linkedin_company;
    }
    if (!linkedinCompany && parsedResults?.steps) {
      const step2 = parsedResults.steps.find(s => s.step === 2);
      if (step2?.data && Object.keys(step2.data).length > 0) {
        linkedinCompany = step2.data;
      }
    }
    
    // Get website data from multiple sources
    if (parsedResults?.website_data) {
      websiteData = parsedResults.website_data;
    } else if (record?.website_extraction_data) {
      try {
        websiteData = typeof record.website_extraction_data === 'string'
          ? JSON.parse(record.website_extraction_data)
          : record.website_extraction_data;
      } catch (e) {
        console.error('Error parsing website_extraction_data:', e);
      }
    }
    // Also try to get from step 6 data
    if (!websiteData && parsedResults?.steps) {
      const step6 = parsedResults.steps.find(s => s.step === 6);
      if (step6?.data) {
        websiteData = {
          emails: step6.data.emails || [],
          phones: step6.data.phones || [],
          addresses: step6.data.addresses || [],
          socialMediaLinks: step6.data.social_links || {}
        };
      }
    }
    
    // Get phone number update info
    if (parsedResults?.phone_number_update) {
      phoneNumberUpdate = parsedResults.phone_number_update;
    } else if (parsedResults?.steps) {
      const step7 = parsedResults.steps.find(s => s.step === 7);
      const step6 = parsedResults.steps.find(s => s.step === 6);
      if (step7?.data?.phone_number_updated) {
        phoneNumberUpdate = {
          updated: step7.data.phone_number_updated,
          original: step7.data.original_phone,
          updated_to: step7.data.updated_phone,
          reason: step7.data.update_reason
        };
      } else if (step6?.data?.toll_free_replaced) {
        phoneNumberUpdate = {
          updated: true,
          original: step6.data.original_phone,
          updated_to: step6.data.replaced_phone,
          reason: 'toll_free_replaced'
        };
      }
    }
    
    // Get phone number history from record
    if (record?.phone_number_history) {
      try {
        const history = typeof record.phone_number_history === 'string'
          ? JSON.parse(record.phone_number_history)
          : record.phone_number_history;
        if (phoneNumberUpdate && Array.isArray(history)) {
          phoneNumberUpdate.history = history;
        }
      } catch (e) {
        console.error('Error parsing phone history:', e);
      }
    }
    
    if (parsedResults?.qualification_reasons) {
      qualificationReasons = parsedResults.qualification_reasons;
    } else if (record?.validation_reason) {
      qualificationReasons = typeof record.validation_reason === 'string'
        ? JSON.parse(record.validation_reason)
        : record.validation_reason;
    }
    
    // Filter out location-related reasons from display
    if (Array.isArray(qualificationReasons)) {
      qualificationReasons = qualificationReasons.filter(reason => {
        const criterion = (reason.criterion || '').toLowerCase();
        return !criterion.includes('location') && 
               !criterion.includes('branch resolution') &&
               !criterion.includes('location match') &&
               !criterion.includes('location validation') &&
               !criterion.includes('data accuracy - location');
      });
    } else {
      qualificationReasons = [];
    }
  } catch (e) {
    console.error('Error parsing data:', e);
  }

  // Get stage status from summary.stages or fall back to 8-step results
  const getStageStatus = (stageId) => {
    // First check for real-time stage status from validation_results
    if (parsedResults?.current_stage !== undefined) {
      const currentStage = parsedResults.current_stage;
      const stageStatus = parsedResults.stage_status || 'pending';
      
      if (stageId === currentStage) {
        // Current stage - return in_progress status
        return stageStatus === 'in_progress' ? 'in_progress' : 
               stageStatus === 'completed' ? 'completed' : 
               stageStatus === 'failed' ? 'failed' : 'pending';
      } else if (stageId < currentStage) {
        // Previous stage - should be completed
        return 'completed';
      } else {
        // Future stage - not started yet
        return 'pending';
      }
    }
    
    // Try to get from new summary.stages first
    if (parsedResults?.summary?.stages) {
      const stages = parsedResults.summary.stages;
      switch (stageId) {
        case 1: return stages.stage_1_data_collection?.status || 'pending';
        case 2: 
          // For Stage 2, check multiple sources
          const stage2Status = stages.stage_2_verification?.status;
          // If status is 'verified' or employment is verified, return verified
          if (stage2Status === 'verified' || stages.stage_2_verification?.employment_verified) {
            return 'verified';
          }
          // If status is 'match' or location matches, return match
          if (stage2Status === 'match' || stages.stage_2_verification?.location_match) {
            return 'match';
          }
          // If rejected, return rejected
          if (stage2Status === 'rejected') {
            return 'rejected';
          }
          // If mismatch, return mismatch
          if (stage2Status === 'mismatch') {
            return 'mismatch';
          }
          return stage2Status || 'pending';
        case 3: return stages.stage_3_validation_rules?.status || 'pending';
        default: return 'pending';
      }
    }
    
    // Fall back to parsing from steps
    if (!parsedResults?.steps) return 'pending';
    
    switch (stageId) {
      case 1: // Data Collection - Steps 1 & 2
        const step1 = parsedResults.steps.find(s => s.step === 1);
        const step2 = parsedResults.steps.find(s => s.step === 2);
        if (step1?.status === 'completed' || step2?.status === 'completed') return 'completed';
        if (step1?.status === 'failed' && step2?.status === 'failed') return 'failed';
        return 'partial';
      
      case 2: // Verification - Steps 3 & 4
        const step3 = parsedResults.steps.find(s => s.step === 3);
        const step4 = parsedResults.steps.find(s => s.step === 4);
        
        // Check if employment is verified (multiple ways to check)
        const employmentVerified = step3?.status === 'verified' || 
                                   step3?.data?.is_employee?.match === true ||
                                   step3?.data?.employment_verified === true ||
                                   (step3?.data && step3.data.employment_status === 'verified');
        
        // Check location match
        const locationMatched = step4?.status === 'match' || 
                               step4?.data?.match === true ||
                               step4?.data?.location_match === true;
        
        // If employment verified, return verified (this is the main check)
        if (employmentVerified) return 'verified';
        
        // If location rejected, return rejected
        if (step4?.status === 'rejected' || step4?.data?.rejected === true) return 'rejected';
        
        // If location matched, return match
        if (locationMatched) return 'match';
        
        // If employment mismatch, return mismatch
        if (step3?.status === 'mismatch' || step3?.data?.is_employee?.match === false) return 'mismatch';
        
        // If location mismatch, return mismatch
        if (step4?.status === 'mismatch' || step4?.data?.match === false) return 'mismatch';
        
        return 'pending';
      
      case 3: // Validation Rules - Step 8
        const step8 = parsedResults.steps.find(s => s.step === 8);
        if (!step8) return 'pending';
        if (step8.data?.qualification_status === 'qualified') return 'qualified';
        if (step8.data?.qualification_status === 'rejected') return 'rejected';
        // If overall status is qualified, this stage passed
        if (parsedResults.overall_status === 'qualified') return 'qualified';
        if (parsedResults.overall_status === 'rejected') return 'rejected';
        return step8.status || 'pending';
      
      default:
        return 'pending';
    }
  };

  const getStageData = (stageId) => {
    // Try to get from new summary.stages first
    if (parsedResults?.summary?.stages) {
      const stages = parsedResults.summary.stages;
      switch (stageId) {
        case 1: 
          return {
            ...stages.stage_1_data_collection,
            linkedin_profile: linkedinProfile,
            linkedin_company: linkedinCompany,
            profile_status: stages.stage_1_data_collection?.linkedin_profile === 'available' ? 'completed' : 'failed',
            company_status: stages.stage_1_data_collection?.linkedin_company === 'available' ? 'completed' : 'failed'
          };
        case 2: 
          return {
            ...stages.stage_2_verification,
            employment_status: stages.stage_2_verification?.employment_verified ? 'verified' : 'mismatch',
            location_status: stages.stage_2_verification?.location_match ? 'match' : 'mismatch',
            employment_reason: stages.stage_2_verification?.employment_verified ? 'Verified from LinkedIn data' : 'Not verified',
            location_reason: stages.stage_2_verification?.rejection_reason || (stages.stage_2_verification?.location_match ? 'Location matches' : 'Location mismatch')
          };
        case 3: 
          const step8 = parsedResults?.steps?.find(s => s.step === 8);
          return {
            ...stages.stage_3_validation_rules,
            ...step8?.data,
            qualification_status: stages.stage_3_validation_rules?.status || step8?.data?.qualification_status
          };
        default: 
          return null;
      }
    }
    
    // Fall back to parsing from steps
    if (!parsedResults?.steps) return null;
    
    switch (stageId) {
      case 1: // Data Collection
        const step1 = parsedResults.steps.find(s => s.step === 1);
        const step2 = parsedResults.steps.find(s => s.step === 2);
        return {
          linkedin_profile: step1?.data || linkedinProfile,
          linkedin_company: step2?.data || linkedinCompany,
          profile_status: step1?.status,
          company_status: step2?.status,
          profile_reason: step1?.reason,
          company_reason: step2?.reason
        };
      
      case 2: // Verification
        const step3 = parsedResults.steps.find(s => s.step === 3);
        const step4 = parsedResults.steps.find(s => s.step === 4);
        return {
          employment: step3?.data,
          employment_status: step3?.status,
          employment_reason: step3?.reason,
          location: step4?.data,
          location_status: step4?.status,
          location_reason: step4?.reason
        };
      
      case 3: // Validation Rules
        const step8 = parsedResults.steps.find(s => s.step === 8);
        return step8?.data || null;
      
      default:
        return null;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
      case 'verified':
      case 'match':
      case 'qualified':
        return '✓';
      case 'failed':
      case 'mismatch':
      case 'rejected':
        return '✗';
      case 'skipped':
      case 'partial':
        return '−';
      default:
        return '•';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
      case 'verified':
      case 'match':
      case 'qualified':
        return 'text-green-400 bg-green-500/20 border-green-500/50';
      case 'failed':
      case 'mismatch':
      case 'rejected':
        return 'text-red-400 bg-red-500/20 border-red-500/50';
      case 'skipped':
      case 'partial':
        return 'text-yellow-400 bg-yellow-500/20 border-yellow-500/50';
      default:
        return 'text-gray-500 bg-gray-600/20 border-gray-600/50';
    }
  };

  const overallStatus = parsedResults?.overall_status || record?.validation_status || 'pending';

  const tabs = [
  { id: 'overview', label: 'Overview' },
  { id: 'field-validation', label: 'Field Validation' },
  { id: 'linkedin', label: 'LinkedIn Data' },
  { id: 'extra', label: 'Extra Data' },
  { id: 'reasons', label: 'Reasons' },
  ];
  
  // Get field validations from parsed results
  let fieldValidations = [];
  try {
    // Try multiple locations where field_validations might be stored
    if (parsedResults?.field_validations) {
      fieldValidations = parsedResults.field_validations;
    } else if (parsedResults?.summary?.field_validations) {
      fieldValidations = parsedResults.summary.field_validations;
    } else if (parsedResults?.perplexity_research?.field_validations) {
      fieldValidations = parsedResults.perplexity_research.field_validations;
    } else if (parsedResults?.steps) {
      // Check step 5 (Perplexity research)
      const step5 = parsedResults.steps.find(s => s.step === 5);
      if (step5?.data?.field_validations) {
        fieldValidations = step5.data.field_validations;
      }
      // Check step 8 (Final validation)
      const step8 = parsedResults.steps.find(s => s.step === 8);
      if (step8?.data?.field_validations) {
        fieldValidations = step8.data.field_validations;
      }
    }
  } catch (e) {
    console.error('Error parsing field validations:', e);
  }

  // Count passed/failed reasons
  const passedReasons = qualificationReasons.filter(r => r.passed).length;
  const failedReasons = qualificationReasons.filter(r => !r.passed).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      
      {/* Modal */}
      <div className="relative w-full max-w-4xl max-h-[85vh] bg-[#0d1117] border border-gray-700 rounded-xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#161b22] to-[#0d1117] px-6 py-4 border-b border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Status Badge */}
              <div className={`px-3 py-1.5 rounded-lg font-bold text-sm flex items-center gap-2 ${
                overallStatus === 'qualified' 
                  ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                  : overallStatus === 'rejected'
                  ? 'bg-red-500/20 text-red-400 border border-red-500/50'
                  : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/50'
              }`}>
                {overallStatus?.toUpperCase()}
              </div>
              
              {/* Person Info */}
              <div>
                <h2 className="text-lg font-semibold text-white">
                  {record?.first_name} {record?.last_name}
                </h2>
                <p className="text-xs text-gray-400">{record?.company_name} • {record?.job_title || 'N/A'}</p>
              </div>
            </div>
            
            <button onClick={onClose} className="p-2 hover:bg-gray-700 rounded-lg transition-colors">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* 3-Stage Pipeline - Clickable */}
          <div className="mt-4 flex items-center justify-center gap-2">
            {VALIDATION_STAGES.map((stage, idx) => {
              let status = getStageStatus(stage.id);
              
              // Show real-time status if validation is in progress
              const currentStage = parsedResults?.current_stage;
              const stageStatus = parsedResults?.stage_status;
              const currentStep = parsedResults?.step;
              
              if (currentStage === stage.id && stageStatus === 'in_progress') {
                status = 'in_progress';
              }
              
              // For Stage 2, if overall status is qualified and we have employment verification, ensure it shows verified
              let displayStatus = status;
              if (stage.id === 2 && overallStatus === 'qualified' && status !== 'verified' && status !== 'rejected' && status !== 'in_progress') {
                // Check if employment is verified from summary or steps
                const stage2Data = parsedResults?.summary?.stages?.stage_2_verification;
                if (stage2Data?.employment_verified || stage2Data?.status === 'verified') {
                  displayStatus = 'verified';
                }
              }
              
              return (
                <div key={stage.id} className="flex items-center">
                  <div 
                    className={`px-4 py-2 rounded-lg cursor-pointer hover:scale-105 transition-all border ${getStatusColor(displayStatus)}`}
                    onClick={() => setSelectedStage(selectedStage === stage.id ? null : stage.id)}
                    title={`${stage.name}: ${displayStatus} - Click to view details`}
                  >
                    <div>
                      <p className="text-xs font-bold">{stage.name}</p>
                      <p className={`text-[10px] opacity-70 ${displayStatus === 'in_progress' ? 'animate-pulse text-blue-400' : ''}`}>
                        {displayStatus === 'in_progress' ? '⏳ IN PROGRESS' : displayStatus.toUpperCase()}
                      </p>
                      {currentStage === stage.id && currentStep && (
                        <p className="text-[9px] text-blue-400 mt-0.5 animate-pulse">
                          {currentStep}...
                        </p>
                      )}
                    </div>
                    {/* Status icon (tick / cross / dot) removed per design; color indicates status now */}
                  </div>
                  {idx < VALIDATION_STAGES.length - 1 && (
                    <div className={`w-8 h-1 mx-1 rounded ${
                      displayStatus === 'completed' || displayStatus === 'verified' || displayStatus === 'qualified' 
                        ? 'bg-green-500/50' 
                        : displayStatus === 'in_progress'
                        ? 'bg-blue-500/50 animate-pulse'
                        : 'bg-gray-600'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-700 bg-[#161b22]">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 text-xs font-medium transition-colors flex items-center gap-1.5 ${
                activeTab === tab.id
                  ? 'text-blue-400 border-b-2 border-blue-400 bg-blue-500/10'
                  : 'text-gray-400 hover:text-gray-300 hover:bg-gray-800'
              }`}
            >
            {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(85vh-220px)] p-4">
          
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-4">
              {/* Record Info Card */}
              <div className="bg-[#161b22] rounded-lg border border-gray-700 p-4">
                <h3 className="text-xs font-semibold text-gray-400 uppercase mb-3">Record Information</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <InfoItem label="Email" value={record?.email || record?.business_email} />
                  <div>
                    <span className="text-[10px] text-gray-500 uppercase">Phone</span>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-xs text-gray-300 font-mono">{record?.phone_number || '-'}</p>
                      {(phoneNumberUpdate?.updated || record?.phone_number_updated) && (
                        <span className="px-1.5 py-0.5 bg-blue-500/20 text-blue-400 text-[9px] font-medium rounded border border-blue-500/30" title="Phone number was updated">
                          🔄 Updated
                        </span>
                      )}
                    </div>
                  </div>
                  <InfoItem label="City" value={record?.city} />
                  <InfoItem label="State" value={record?.state} />
                  <InfoItem label="Country" value={record?.country} />
                  <InfoItem label="LinkedIn" value={record?.linkedin_url ? 'Available' : 'N/A'} isLink={record?.linkedin_url} />
                  <InfoItem label="Company LinkedIn" value={record?.linkedin_url_company ? 'Available' : 'N/A'} isLink={record?.linkedin_url_company} />
                  <InfoItem label="Domain" value={record?.domain || record?.company_website} />
                </div>
              </div>

              {/* 3-Stage Validation Details */}
              <div className="bg-[#161b22] rounded-lg border border-gray-700 p-4">
                <h3 className="text-xs font-semibold text-gray-400 uppercase mb-3">Validation Stages</h3>
                <div className="space-y-3">
                  {(() => {
                    // Get real-time status info for all stages
                    const currentStage = parsedResults?.current_stage;
                    const currentStep = parsedResults?.step;
                    
                    return VALIDATION_STAGES.map(stage => {
                      const status = getStageStatus(stage.id);
                      const stageData = getStageData(stage.id);
                      return (
                        <div 
                          key={stage.id} 
                          className={`p-4 rounded-lg border cursor-pointer hover:opacity-90 transition-opacity ${getStatusColor(status)}`}
                          onClick={() => setSelectedStage(selectedStage === stage.id ? null : stage.id)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className="text-2xl">{stage.icon}</span>
                              <div>
                                <p className="text-sm font-bold text-white">Stage {stage.id}: {stage.name}</p>
                                <p className="text-xs text-gray-400">{stage.description}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <span className={`text-sm font-bold uppercase ${
                                status === 'completed' || status === 'verified' || status === 'qualified' ? 'text-green-400' :
                                status === 'failed' || status === 'mismatch' || status === 'rejected' ? 'text-red-400' :
                                status === 'in_progress' ? 'text-blue-400 animate-pulse' :
                                'text-gray-400'
                              }`}>
                                {status === 'in_progress' ? '⏳ IN PROGRESS' : status.toUpperCase()}
                              </span>
                              {currentStage === stage.id && currentStep && (
                                <p className="text-[10px] text-blue-400 mt-1 animate-pulse">
                                  {currentStep}...
                                </p>
                              )}
                              <p className="text-[10px] text-gray-500 mt-1">Click for details →</p>
                            </div>
                          </div>
                        
                          {/* Quick summary */}
                          {stage.id === 1 && stageData && (
                            <div className="mt-2 pt-2 border-t border-gray-600/50 text-xs text-gray-400">
                              Profile: {stageData.profile_status || 'N/A'} | Company: {stageData.company_status || 'N/A'}
                            </div>
                          )}
                          {stage.id === 2 && stageData && (
                            <div className="mt-2 pt-2 border-t border-gray-600/50 text-xs text-gray-400">
                              Employment: {stageData.employment_status || 'N/A'} | Location: {stageData.location_status || 'N/A'}
                            </div>
                          )}
                          {stage.id === 3 && stageData && (
                            <div className="mt-2 pt-2 border-t border-gray-600/50 text-xs text-gray-400">
                              Qualification: {stageData.qualification_status || 'N/A'} | Confidence: {stageData.confidence_score || 0}%
                            </div>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-2">
                <StatCard label="Total Stages" value="3" color="blue" />
                <StatCard label="Passed Reasons" value={passedReasons} color="green" />
                <StatCard label="Failed Reasons" value={failedReasons} color="red" />
              </div>
            </div>
          )}

          {/* Field Validation Tab */}
          {activeTab === 'field-validation' && (
            <div className="space-y-4">
              {/* Location Test Case Results */}
              {parsedResults?.steps?.find(s => s.step === 4)?.data?.location_test_result && (
                <div className={`bg-[#161b22] rounded-lg border p-4 ${
                  parsedResults.steps.find(s => s.step === 4).data.location_test_result === 'PASSED' 
                    ? 'border-green-500/30' 
                    : 'border-red-500/30'
                }`}>
                  <h3 className="text-xs font-semibold uppercase mb-3 flex items-center gap-2">
                    <span>📍</span> Location Validation Test Case
                  </h3>
                  {(() => {
                    const step4Data = parsedResults.steps.find(s => s.step === 4)?.data;
                    const testDetails = step4Data?.location_test_details;
                    return (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-400">Test Result:</span>
                          <span className={`text-xs font-bold px-2 py-1 rounded ${
                            step4Data.location_test_result === 'PASSED'
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-red-500/20 text-red-400'
                          }`}>
                            {step4Data.location_test_result === 'PASSED' ? '✅ PASSED' : '❌ FAILED'}
                          </span>
                        </div>
                        {testDetails && (
                          <>
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-400">Person Location Match:</span>
                              <span className={`text-xs ${testDetails.person_location_match ? 'text-green-400' : 'text-red-400'}`}>
                                {testDetails.person_location_match ? '✓ Match' : '✗ No Match'}
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-400">Company Location Match:</span>
                              <span className={`text-xs ${testDetails.company_location_match ? 'text-green-400' : 'text-red-400'}`}>
                                {testDetails.company_location_match ? '✓ Match' : '✗ No Match'}
                              </span>
                            </div>
                            {testDetails.matched_rule_locations?.length > 0 && (
                              <div>
                                <span className="text-xs text-gray-400">Matched Rules:</span>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {testDetails.matched_rule_locations.map((rule, i) => (
                                    <span key={i} className="text-[10px] px-2 py-0.5 bg-blue-500/20 text-blue-300 rounded">
                                      {rule}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
              
              <div className="bg-[#161b22] rounded-lg border border-gray-700 p-4">
                <h3 className="text-xs font-semibold text-gray-400 uppercase mb-4 flex items-center gap-2">
                  <span>✅</span> Field-by-Field Validation
                </h3>
                {fieldValidations && fieldValidations.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="border-b border-gray-700">
                          <th className="text-[10px] text-gray-500 uppercase p-2 font-semibold">Field</th>
                          <th className="text-[10px] text-gray-500 uppercase p-2 font-semibold">Original Record</th>
                          <th className="text-[10px] text-gray-500 uppercase p-2 font-semibold">Status</th>
                          <th className="text-[10px] text-gray-500 uppercase p-2 font-semibold">Finding</th>
                          <th className="text-[10px] text-gray-500 uppercase p-2 font-semibold">Action</th>
                          <th className="text-[10px] text-gray-500 uppercase p-2 font-semibold">Evidence</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fieldValidations.map((validation, i) => {
                          const getStatusBadge = (status) => {
                            const statusMap = {
                              'CORRECT': { bg: 'bg-green-500/20', text: 'text-green-400', icon: '✓', label: 'CORRECT' },
                              'INCORRECT': { bg: 'bg-red-500/20', text: 'text-red-400', icon: 'X', label: 'INCORRECT' },
                              'INCOMPLETE': { bg: 'bg-yellow-500/20', text: 'text-yellow-400', icon: 'i', label: 'INCOMPLETE' },
                              'INFERRED': { bg: 'bg-blue-500/20', text: 'text-blue-400', icon: 'i', label: 'INFERRED' },
                              'UNVERIFIED': { bg: 'bg-orange-500/20', text: 'text-orange-400', icon: '!', label: 'UNVERIFIED' },
                              'ALTERNATE_DOMAIN': { bg: 'bg-purple-500/20', text: 'text-purple-400', icon: '!', label: 'ALTERNATE DOMAIN' }
                            };
                            const config = statusMap[status] || { bg: 'bg-gray-500/20', text: 'text-gray-400', icon: '?', label: status };
                            return (
                              <span className={`px-2 py-1 rounded text-[10px] font-semibold ${config.bg} ${config.text}`}>
                                {config.icon} {config.label}
                              </span>
                            );
                          };
                          
                          const formatSources = (sources, sourceCount) => {
                            if (!sources || sources.length === 0) return 'No sources';
                            
                            // Map source names to display names
                            const sourceMap = {
                              'linkedin': 'linkedin',
                              'perplexity_deep_search': 'perplexity',
                              'company_website': record.domain || record.company_website || 'company_website',
                              'contactout': 'contactout'
                            };
                            
                            // Get unique sources with proper mapping
                            const uniqueSources = [...new Set(sources.map(s => {
                              const mapped = sourceMap[s] || s;
                              // Extract domain from company_website if it's a URL
                              if (mapped === 'company_website' && record.domain) {
                                return record.domain.replace(/^https?:\/\//, '').split('/')[0];
                              }
                              return mapped;
                            }))];
                            
                            const count = sourceCount || uniqueSources.length;
                            const primarySource = uniqueSources[0] || 'unknown';
                            const additionalCount = count - 1;
                            
                            // Format: "linkedin +2" or "perplexity +1" or "marketwavegen +1"
                            if (additionalCount > 0) {
                              return `${primarySource} +${additionalCount}`;
                            } else {
                              return primarySource;
                            }
                          };
                          
                          return (
                            <tr key={i} className="border-b border-gray-800 hover:bg-gray-800/30">
                              <td className="p-2">
                                <span className="text-xs font-medium text-gray-300">{validation.field}</span>
                              </td>
                              <td className="p-2">
                                <span className="text-xs text-gray-400">{validation.original_value || '(not specified)'}</span>
                              </td>
                              <td className="p-2">
                                {getStatusBadge(validation.status)}
                              </td>
                              <td className="p-2">
                                <span className="text-[11px] text-gray-400">{validation.finding || 'No finding available'}</span>
                              </td>
                              <td className="p-2">
                                {validation.action && validation.action !== 'No action needed' && validation.corrected_value ? (
                                  <button
                                    onClick={async () => {
                                      if (!validation.corrected_value) {
                                        alert('No corrected value available to update');
                                        return;
                                      }
                                      
                                      if (window.confirm(`Update ${validation.field} from "${validation.original_value}" to "${validation.corrected_value}"?`)) {
                                        setUpdatingField(validation.field);
                                        try {
                                          const { updateRecordField } = await import('../services/api');
                                          
                                          // Get task_id - try multiple sources
                                          const taskIdToUse = recordTaskId || 
                                                             record.task_id || 
                                                             (window.location.pathname.match(/\/tasks\/(\d+)/)?.[1]);
                                          
                                          if (!taskIdToUse) {
                                            alert('Error: Could not determine task ID. Please refresh the page and try again.');
                                            setUpdatingField(null);
                                            return;
                                          }
                                          
                                          const response = await updateRecordField(
                                            parseInt(taskIdToUse),
                                            record.id,
                                            validation.field,
                                            validation.corrected_value
                                          );
                                          
                                          if (response.success) {
                                            alert(`✓ ${validation.field} updated successfully!`);
                                            setUpdatingField(null);
                                            // Reload the page to show updated data
                                            window.location.reload();
                                          } else {
                                            alert(`Failed to update: ${response.message || 'Unknown error'}`);
                                            setUpdatingField(null);
                                          }
                                        } catch (error) {
                                          console.error('Error updating field:', error);
                                          alert(`Error updating field: ${error.message || 'Unknown error'}`);
                                          setUpdatingField(null);
                                        }
                                      }
                                    }}
                                    disabled={updatingField === validation.field}
                                    className={`px-2 py-1 text-[10px] rounded border transition-colors ${
                                      updatingField === validation.field
                                        ? 'bg-blue-500/40 text-blue-300 border-blue-500/50 cursor-wait'
                                        : 'bg-blue-500/20 text-blue-400 border-blue-500/30 hover:bg-blue-500/30 cursor-pointer'
                                    }`}
                                  >
                                    {updatingField === validation.field ? 'Updating...' : validation.action}
                                  </button>
                                ) : (
                                  <span className="text-[11px] text-gray-500">{validation.action || 'No action'}</span>
                                )}
                              </td>
                              <td className="p-2">
                                <span className="text-[10px] text-gray-500">{formatSources(validation.sources, validation.source_count)}</span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-xs text-gray-500">No field validations available</p>
                    <p className="text-[10px] text-gray-600 mt-1">Field-by-field validation will appear here after validation completes</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* LinkedIn Data Tab */}
          {activeTab === 'linkedin' && (
            <div className="space-y-4">
              {/* Profile Data */}
              <div className="bg-[#161b22] rounded-lg border border-blue-500/30 p-4">
                <h3 className="text-xs font-semibold text-blue-400 uppercase mb-3 flex items-center gap-2">
                  <span>👤</span> LinkedIn Profile
                </h3>
                {linkedinProfile && Object.keys(linkedinProfile).length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <InfoItem label="Name" value={linkedinProfile.name || linkedinProfile.full_name || '-'} />
                    <InfoItem label="Location" value={linkedinProfile.location || '-'} />
                    <InfoItem label="City" value={linkedinProfile.city || '-'} />
                    <InfoItem label="Country Code" value={linkedinProfile.country_code || '-'} />
                    <InfoItem label="Company" value={linkedinProfile.current_company?.name || linkedinProfile.company || '-'} />
                    <InfoItem label="Headline" value={linkedinProfile.headline || '-'} />
                    <InfoItem label="About" value={linkedinProfile.about ? linkedinProfile.about.substring(0, 100) + '...' : '-'} />
                    <InfoItem label="Followers" value={linkedinProfile.followers ? linkedinProfile.followers.toLocaleString() : '-'} />
                    <InfoItem label="Connections" value={linkedinProfile.connections || linkedinProfile.connection_count || '-'} />
                    {linkedinProfile.experience && linkedinProfile.experience.length > 0 && (
                      <div className="col-span-full">
                        <span className="text-[10px] text-gray-500 uppercase">Experience</span>
                        <div className="mt-1 space-y-1">
                          {linkedinProfile.experience.slice(0, 3).map((exp, i) => (
                            <p key={i} className="text-xs text-gray-300">
                              {exp.title || exp.position || '-'} at {exp.company || '-'}
                            </p>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-500">No profile data available from LinkedIn scraping</p>
                    {record?.linkedin_url && (
                      <p className="text-[10px] text-gray-600">
                        LinkedIn URL: <a href={record.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">{record.linkedin_url}</a>
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Company Data */}
              <div className="bg-[#161b22] rounded-lg border border-purple-500/30 p-4">
                <h3 className="text-xs font-semibold text-purple-400 uppercase mb-3 flex items-center gap-2">
                  <span>🏢</span> LinkedIn Company
                </h3>
                {linkedinCompany && (linkedinCompany.name || linkedinCompany.id) ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <InfoItem label="Name" value={linkedinCompany.name || linkedinCompany.id || '-'} />
                    <InfoItem label="Industry" value={linkedinCompany.industry || '-'} />
                    <InfoItem label="Industries" value={linkedinCompany.industries || linkedinCompany.industry || '-'} />
                    <InfoItem label="Size" value={
                      linkedinCompany.company_size || 
                      (linkedinCompany.employees_in_linkedin ? `${linkedinCompany.employees_in_linkedin} employees` : '-')
                    } />
                    <InfoItem label="Website" value={linkedinCompany.website || '-'} isLink={linkedinCompany.website} />
                    <InfoItem label="Type" value={linkedinCompany.type || '-'} />
                    <InfoItem label="Organization Type" value={linkedinCompany.organization_type || linkedinCompany.type || '-'} />
                    <InfoItem label="Headquarters" value={linkedinCompany.headquarters || '-'} />
                    <InfoItem label="Founded" value={linkedinCompany.founded || '-'} />
                    <InfoItem label="Followers" value={
                      linkedinCompany.followers 
                        ? (typeof linkedinCompany.followers === 'number' 
                            ? linkedinCompany.followers.toLocaleString() 
                            : linkedinCompany.followers)
                        : '-'
                    } />
                    <InfoItem label="Country" value={linkedinCompany.country_code || '-'} />
                    {linkedinCompany.locations && linkedinCompany.locations.length > 0 && (
                      <div className="col-span-full">
                        <span className="text-[10px] text-gray-500 uppercase">Locations ({linkedinCompany.locations.length})</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {linkedinCompany.locations.slice(0, 5).map((loc, i) => (
                            <span key={i} className="px-2 py-0.5 bg-purple-500/20 text-purple-300 rounded text-[10px]">
                              {typeof loc === 'string' ? loc : (loc?.address || loc?.name || JSON.stringify(loc))}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {linkedinCompany.description && (
                      <div className="col-span-full">
                        <span className="text-[10px] text-gray-500 uppercase">Description</span>
                        <p className="text-xs text-gray-300 mt-1">{linkedinCompany.description.substring(0, 200)}...</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-500">No company data available from LinkedIn scraping</p>
                    {record?.linkedin_url_company && (
                      <p className="text-[10px] text-gray-600">
                        Company LinkedIn URL: <a href={record.linkedin_url_company} target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:underline">{record.linkedin_url_company}</a>
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Extra Data Tab (Website, Phone Updates) */}
          {activeTab === 'extra' && (
            <div className="space-y-4">
              {/* Phone Number Update Status */}
              <div className="bg-[#161b22] rounded-lg border border-blue-500/30 p-4">
                <h3 className="text-xs font-semibold text-blue-400 uppercase mb-3 flex items-center gap-2">
                  <span>📞</span> Phone Number Status
                </h3>
                {(phoneNumberUpdate?.updated || record?.phone_number_updated) ? (
                  <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs font-bold rounded">🔄 UPDATED</span>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div>
                        <span className="text-gray-500">Original:</span>
                        <p className="text-red-300 mt-1 font-mono line-through">{phoneNumberUpdate?.original || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Updated To:</span>
                        <p className="text-green-300 mt-1 font-mono font-bold">{phoneNumberUpdate?.updated_to || record?.phone_number}</p>
                      </div>
                      <div className="col-span-full">
                        <span className="text-gray-500">Reason:</span>
                        <p className="text-gray-300 mt-1 capitalize">{(phoneNumberUpdate?.reason || 'toll_free_replaced').replace(/_/g, ' ')}</p>
                      </div>
                    </div>
                    {/* Phone Update History */}
                    {phoneNumberUpdate?.history && phoneNumberUpdate.history.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-blue-500/30">
                        <span className="text-[10px] text-gray-500 uppercase">Update History</span>
                        <div className="space-y-1 mt-1">
                          {phoneNumberUpdate.history.map((h, i) => (
                            <div key={i} className="text-[10px] text-gray-400 flex items-center gap-2">
                              <span className="text-gray-500">{new Date(h.updated_at).toLocaleString()}</span>
                              <span className="font-mono">{h.from}</span>
                              <span>→</span>
                              <span className="font-mono text-green-400">{h.to}</span>
                              <span className="text-gray-600">({h.reason})</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="p-3 bg-gray-500/10 rounded-lg border border-gray-500/30 text-center">
                    <span className="text-gray-400 text-xs">Phone number not updated</span>
                    <p className="text-gray-500 text-[10px] mt-1">Current: {record?.phone_number || 'N/A'}</p>
                  </div>
                )}
              </div>
              
              {/* Website Data */}
              <div className="bg-[#161b22] rounded-lg border border-teal-500/30 p-4">
                <h3 className="text-xs font-semibold text-teal-400 uppercase mb-3 flex items-center gap-2">
                  <span>🌐</span> Website Scraped Data (Extra Info)
                </h3>
                {websiteData && (websiteData.emails?.length > 0 || websiteData.phones?.length > 0 || websiteData.addresses?.length > 0) ? (
                  <div className="space-y-3">
                    {/* Summary Stats */}
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className="px-2 py-1 bg-teal-500/20 text-teal-300 text-[10px] rounded">
                        📧 {websiteData.emails?.length || 0} emails
                      </span>
                      <span className="px-2 py-1 bg-teal-500/20 text-teal-300 text-[10px] rounded">
                        📞 {websiteData.phones?.length || 0} phones
                      </span>
                      <span className="px-2 py-1 bg-teal-500/20 text-teal-300 text-[10px] rounded">
                        📍 {websiteData.addresses?.length || 0} addresses
                      </span>
                    </div>
                    
                    {websiteData.emails && websiteData.emails.length > 0 && (
                      <div>
                        <span className="text-[10px] text-gray-500 uppercase">Emails</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {websiteData.emails.slice(0, 5).map((email, i) => (
                            <span key={i} className="px-2 py-0.5 bg-teal-500/20 text-teal-300 rounded text-[10px]">{email}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {websiteData.phones && websiteData.phones.length > 0 && (
                      <div>
                        <span className="text-[10px] text-gray-500 uppercase">Phone Numbers</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {websiteData.phones.slice(0, 5).map((phone, i) => (
                            <span key={i} className="px-2 py-0.5 bg-teal-500/20 text-teal-300 rounded text-[10px] font-mono">{phone}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {websiteData.addresses && websiteData.addresses.length > 0 && (
                      <div>
                        <span className="text-[10px] text-gray-500 uppercase">Addresses</span>
                        <div className="space-y-1 mt-1">
                          {websiteData.addresses.slice(0, 3).map((addr, i) => (
                            <p key={i} className="text-[10px] text-gray-400">{addr}</p>
                          ))}
                        </div>
                      </div>
                    )}
                    {websiteData.socialMediaLinks && Object.keys(websiteData.socialMediaLinks).length > 0 && (
                      <div>
                        <span className="text-[10px] text-gray-500 uppercase">Social Media</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {Object.entries(websiteData.socialMediaLinks).map(([platform, url], i) => (
                            <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="px-2 py-0.5 bg-purple-500/20 text-purple-300 rounded text-[10px] hover:bg-purple-500/30">
                              {platform}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-xs text-gray-500">No website data available</p>
                    <p className="text-[10px] text-gray-600 mt-1">Website scraping may not have been performed or no data was found</p>
                  </div>
                )}
              </div>
              
              {/* Toll-Free Validation Results */}
              {parsedResults?.toll_free_validation && (
                <div className="bg-[#161b22] rounded-lg border border-yellow-500/30 p-4">
                  <h3 className="text-xs font-semibold text-yellow-400 uppercase mb-3 flex items-center gap-2">
                    <span>📱</span> Toll-Free Number Analysis
                  </h3>
                  <div className="space-y-2">
                    {parsedResults.toll_free_validation.toll_free_numbers?.length > 0 && (
                      <div>
                        <span className="text-[10px] text-gray-500 uppercase">Toll-Free Numbers</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {parsedResults.toll_free_validation.toll_free_numbers.map((num, i) => (
                            <span key={i} className="px-2 py-0.5 bg-red-500/20 text-red-300 rounded text-[10px] font-mono">{num}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {parsedResults.toll_free_validation.direct_numbers?.length > 0 && (
                      <div>
                        <span className="text-[10px] text-gray-500 uppercase">Direct Numbers</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {parsedResults.toll_free_validation.direct_numbers.map((num, i) => (
                            <span key={i} className="px-2 py-0.5 bg-green-500/20 text-green-300 rounded text-[10px] font-mono">{num}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {parsedResults.toll_free_validation.recommended_contact && (
                      <div className="mt-2 p-2 bg-blue-500/10 rounded border border-blue-500/30">
                        <span className="text-[10px] text-gray-500 uppercase">Recommended Contact</span>
                        <p className="text-xs text-blue-300 mt-1">{parsedResults.toll_free_validation.recommended_contact}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Reasons Tab */}
          {activeTab === 'reasons' && (
            <div className="space-y-4">
              {/* Data Corrections Section */}
              {parsedResults?.perplexity_research?.data_corrections && (
                <div className="bg-[#161b22] rounded-lg border border-orange-500/30 p-4">
                  <h3 className="text-xs font-semibold text-orange-400 uppercase mb-3">
                    Data Accuracy Check (Record vs LinkedIn)
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    {['name', 'company', 'title', 'location'].map((field) => {
                      const corrections = parsedResults.perplexity_research.data_corrections;
                      const isCorrect = corrections[`${field}_correct`];
                      const correctValue = corrections[`correct_${field}`];
                      return (
                        <div key={field} className={`p-2 rounded border ${isCorrect ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] text-gray-500 uppercase">{field}</span>
                            <span className={`text-[10px] ${isCorrect ? 'text-green-400' : 'text-red-400'}`}>
                              {isCorrect ? '✓ Correct' : '✗ Mismatch'}
                            </span>
                          </div>
                          {!isCorrect && correctValue && (
                            <p className="text-[10px] text-orange-300 mt-1">
                              Should be: {correctValue}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Qualification Reasons */}
              <div className="bg-[#161b22] rounded-lg border border-gray-700 p-4">
                <h3 className="text-xs font-semibold text-gray-400 uppercase mb-3">
                  Qualification Reasons
                </h3>
                {qualificationReasons && qualificationReasons.length > 0 ? (
                  <div className="space-y-2">
                    {qualificationReasons.map((reason, i) => (
                      <div key={i} className={`p-3 rounded-lg border ${
                        reason.passed 
                          ? 'bg-green-500/10 border-green-500/30' 
                          : 'bg-red-500/10 border-red-500/30'
                      }`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-xs font-bold ${reason.passed ? 'text-green-400' : 'text-red-400'}`}>
                            {reason.criterion || `Criterion ${i + 1}`}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            reason.passed 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-red-500/20 text-red-400'
                          }`}>
                            {reason.passed ? '✓ PASS' : '✗ FAIL'}
                          </span>
                        </div>
                        <p className="text-[11px] text-gray-400">{reason.reason}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-500">No qualification reasons available</p>
                )}
              </div>
              
              {/* AI Research Context */}
              {parsedResults?.steps?.find(s => s.step === 5)?.data?.linkedin_context_used && (
                <div className="bg-[#161b22] rounded-lg border border-blue-500/30 p-4">
                  <h3 className="text-xs font-semibold text-blue-400 uppercase mb-2">
                    AI Research Context
                  </h3>
                  <div className="flex gap-2">
                    <span className={`px-2 py-1 text-[10px] rounded ${
                      parsedResults.steps.find(s => s.step === 5).data.linkedin_context_used.profile_available 
                        ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                    }`}>
                      LinkedIn Profile: {parsedResults.steps.find(s => s.step === 5).data.linkedin_context_used.profile_available ? '✓ Used' : '✗ Not Available'}
                    </span>
                    <span className={`px-2 py-1 text-[10px] rounded ${
                      parsedResults.steps.find(s => s.step === 5).data.linkedin_context_used.company_available 
                        ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                    }`}>
                      LinkedIn Company: {parsedResults.steps.find(s => s.step === 5).data.linkedin_context_used.company_available ? '✓ Used' : '✗ Not Available'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 bg-[#161b22] border-t border-gray-700 flex items-center justify-between">
          <div className="text-[10px] text-gray-500">
            {parsedResults?.completed_at && `Completed: ${new Date(parsedResults.completed_at).toLocaleString()}`}
          </div>
          <button
            onClick={onClose}
            className="px-4 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-xs font-medium rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
      
      {/* Stage Detail Modal */}
      {selectedStage && (() => {
        const stageData = getStageData(selectedStage);
        const stageInfo = VALIDATION_STAGES.find(s => s.id === selectedStage);
        const status = getStageStatus(selectedStage);
        
        return (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/80" onClick={() => setSelectedStage(null)} />
            <div className="relative w-full max-w-2xl max-h-[80vh] bg-[#0d1117] border border-gray-700 rounded-xl shadow-2xl overflow-hidden">
              <div className="bg-[#161b22] px-6 py-4 border-b border-gray-700 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <span>{stageInfo?.icon}</span> Stage {selectedStage}: {stageInfo?.name}
                </h3>
                <button onClick={() => setSelectedStage(null)} className="p-2 hover:bg-gray-700 rounded-lg">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="overflow-y-auto max-h-[calc(80vh-120px)] p-6">
                {/* Status */}
                <div className={`p-4 rounded-lg border mb-4 ${getStatusColor(status)}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-bold">Status</span>
                    <span className="text-lg font-bold">{status.toUpperCase()}</span>
                  </div>
                </div>
                
                {/* Stage-specific content */}
                {selectedStage === 1 && stageData && (
                  <div className="space-y-4">
                    <div className="bg-[#161b22] rounded-lg border border-gray-700 p-4">
                      <h4 className="text-xs text-gray-500 uppercase mb-2">LinkedIn Profile</h4>
                      <p className="text-xs text-gray-400 mb-2">Status: {stageData.profile_status || 'N/A'}</p>
                      {stageData.profile_reason && <p className="text-xs text-gray-500">{stageData.profile_reason}</p>}
                      {stageData.linkedin_profile && (
                        <pre className="text-[10px] text-gray-400 mt-2 bg-gray-900/50 p-2 rounded overflow-auto max-h-32">
                          {JSON.stringify(stageData.linkedin_profile, null, 2)}
                        </pre>
                      )}
                    </div>
                    <div className="bg-[#161b22] rounded-lg border border-gray-700 p-4">
                      <h4 className="text-xs text-gray-500 uppercase mb-2">LinkedIn Company</h4>
                      <p className="text-xs text-gray-400 mb-2">Status: {stageData.company_status || 'N/A'}</p>
                      {stageData.company_reason && <p className="text-xs text-gray-500">{stageData.company_reason}</p>}
                      {stageData.linkedin_company && (
                        <pre className="text-[10px] text-gray-400 mt-2 bg-gray-900/50 p-2 rounded overflow-auto max-h-32">
                          {JSON.stringify(stageData.linkedin_company, null, 2)}
                        </pre>
                      )}
                    </div>
                  </div>
                )}
                
                {selectedStage === 2 && stageData && (
                  <div className="space-y-4">
                    <div className={`bg-[#161b22] rounded-lg border p-4 ${
                      stageData.employment_status === 'verified' ? 'border-green-500/30' : 
                      stageData.employment_status === 'mismatch' ? 'border-red-500/30' : 'border-gray-700'
                    }`}>
                      <h4 className="text-xs text-gray-500 uppercase mb-2">Employment Verification</h4>
                      <p className={`text-sm font-bold mb-2 ${
                        stageData.employment_status === 'verified' ? 'text-green-400' :
                        stageData.employment_status === 'mismatch' ? 'text-red-400' : 'text-gray-400'
                      }`}>
                        {stageData.employment_status?.toUpperCase() || 'PENDING'}
                      </p>
                      {stageData.employment_reason && <p className="text-xs text-gray-400">{stageData.employment_reason}</p>}
                      {stageData.employment && (
                        <pre className="text-[10px] text-gray-400 mt-2 bg-gray-900/50 p-2 rounded overflow-auto max-h-32">
                          {JSON.stringify(stageData.employment, null, 2)}
                        </pre>
                      )}
                    </div>
                    <div className={`bg-[#161b22] rounded-lg border p-4 ${
                      stageData.location_status === 'match' ? 'border-green-500/30' : 
                      stageData.location_status === 'mismatch' ? 'border-yellow-500/30' : 'border-gray-700'
                    }`}>
                      <h4 className="text-xs text-gray-500 uppercase mb-2">Location Match</h4>
                      <p className={`text-sm font-bold mb-2 ${
                        stageData.location_status === 'match' ? 'text-green-400' :
                        stageData.location_status === 'mismatch' ? 'text-yellow-400' : 'text-gray-400'
                      }`}>
                        {stageData.location_status?.toUpperCase() || 'PENDING'}
                      </p>
                      {stageData.location_reason && <p className="text-xs text-gray-400">{stageData.location_reason}</p>}
                      {stageData.location && (
                        <pre className="text-[10px] text-gray-400 mt-2 bg-gray-900/50 p-2 rounded overflow-auto max-h-32">
                          {JSON.stringify(stageData.location, null, 2)}
                        </pre>
                      )}
                    </div>
                  </div>
                )}
                
                {selectedStage === 3 && stageData && (
                  <div className="space-y-4">
                    <div className="bg-[#161b22] rounded-lg border border-gray-700 p-4">
                      <h4 className="text-xs text-gray-500 uppercase mb-2">Qualification Result</h4>
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                          <span className="text-[10px] text-gray-500">Status</span>
                          <p className={`text-sm font-bold ${
                            stageData.qualification_status === 'qualified' ? 'text-green-400' : 'text-red-400'
                          }`}>
                            {stageData.qualification_status?.toUpperCase() || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <span className="text-[10px] text-gray-500">Confidence</span>
                          <p className="text-sm font-bold text-blue-400">{stageData.confidence_score || 0}%</p>
                        </div>
                        <div>
                          <span className="text-[10px] text-gray-500">Passed Rules</span>
                          <p className="text-sm font-bold text-green-400">{stageData.passed_rules || 0}</p>
                        </div>
                        <div>
                          <span className="text-[10px] text-gray-500">Failed Rules</span>
                          <p className="text-sm font-bold text-red-400">{stageData.failed_rules || 0}</p>
                        </div>
                      </div>
                      {stageData.overall_assessment && (
                        <div className="mt-3 p-3 bg-gray-900/50 rounded">
                          <span className="text-[10px] text-gray-500 uppercase">Assessment</span>
                          <p className="text-xs text-gray-300 mt-1">{stageData.overall_assessment}</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Rules Details */}
                    {stageData.rules_details && stageData.rules_details.length > 0 && (
                      <div className="bg-[#161b22] rounded-lg border border-blue-500/30 p-4">
                        <h4 className="text-xs text-blue-400 uppercase mb-3">Validation Rules Applied (AI Analysis)</h4>
                        <div className="space-y-2">
                          {stageData.rules_details.map((rule, i) => (
                            <div key={i} className={`p-2 rounded border ${
                              rule.passed ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'
                            }`}>
                              <div className="flex items-center justify-between">
                                <span className={`text-xs font-medium ${rule.passed ? 'text-green-400' : 'text-red-400'}`}>
                                  {rule.criterion || rule.rule || `Rule ${i + 1}`}
                                </span>
                                <span className={`text-xs ${rule.passed ? 'text-green-400' : 'text-red-400'}`}>
                                  {rule.passed ? '✓ PASS' : '✗ FAIL'}
                                </span>
                              </div>
                              {rule.reason && (
                                <p className="text-[10px] text-gray-400 mt-1">{rule.reason}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Data Sources Used */}
                    {stageData.data_sources_used && (
                      <div className="bg-[#161b22] rounded-lg border border-gray-700 p-4">
                        <h4 className="text-xs text-gray-500 uppercase mb-2">Data Sources Used</h4>
                        <div className="flex flex-wrap gap-2">
                          {stageData.data_sources_used.map((source, i) => (
                            <span key={i} className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded text-[10px]">
                              {source.replace(/_/g, ' ')}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Additional Findings */}
                    {stageData.additional_findings && (
                      <div className="bg-[#161b22] rounded-lg border border-purple-500/30 p-4">
                        <h4 className="text-xs text-purple-400 uppercase mb-2">Additional Findings</h4>
                        <p className="text-xs text-gray-300">{stageData.additional_findings}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="px-6 py-3 bg-[#161b22] border-t border-gray-700 flex justify-end">
                <button
                  onClick={() => setSelectedStage(null)}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white text-xs font-medium rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

// Helper Components
const InfoItem = ({ label, value, isLink }) => (
  <div>
    <span className="text-[10px] text-gray-500 uppercase">{label}</span>
    {isLink ? (
      <a href={isLink} target="_blank" rel="noopener noreferrer" className="block text-xs text-blue-400 hover:underline truncate">
        {value || '-'}
      </a>
    ) : (
      <p className="text-xs text-gray-300 truncate">{value || '-'}</p>
    )}
  </div>
);

const StatCard = ({ label, value, color }) => {
  const colors = {
    blue: 'bg-blue-500/20 border-blue-500/30 text-blue-400',
    green: 'bg-green-500/20 border-green-500/30 text-green-400',
    red: 'bg-red-500/20 border-red-500/30 text-red-400',
    gray: 'bg-gray-500/20 border-gray-500/30 text-gray-400',
  };
  return (
    <div className={`p-3 rounded-lg border text-center ${colors[color]}`}>
      <p className="text-lg font-bold">{value}</p>
      <p className="text-[10px] uppercase opacity-70">{label}</p>
    </div>
  );
};

export default ValidationDetailModal;
